import React, { useContext, useEffect, useState } from 'react';
import { AuthenticationContext } from './AuthenticationProvider';
import { trackPromise } from 'react-promise-tracker';
import { getConfigActions, getResourceActions } from './api';
import { ResourceAction } from './types/ResourceAction';
import { ConfigAction } from './types/ConfigAction';
import { ResourceActionsResponse } from './types/ResourceActionsResponse';
import { ConfigActionsResponse } from './types/ConfigActionsResponse';
import { ResourceType } from './types/Resource';
import moment from 'moment/moment';
import { ActionCableContext } from './ActionCableProvider';
import {numericSortObjectArray} from '../utility'

export type ActivityData = {
  id: number;
  type: ResourceType | 'config';
  name: string;
  activityType: string;
  activityMessage: string;
  userFirstName: string;
  dateTime: number;
};

export interface IActivityContext {
  activities: ActivityData[];
}

export interface IActivityProviderProps {
  children: any;
  projectId: number;
}

const ActivityContext = React.createContext<IActivityContext>({
  activities: [],
});

const createActivityData = (
  resourceActions: ResourceAction[],
  configActions: ConfigAction[]
) => {
  let arr: ActivityData[] = [];

  resourceActions.forEach((action) => {
    arr.push({
      id: action.id,
      type: action.resource_type,
      name: action.resource_guid,
      activityType: action.action_type,
      activityMessage: action.action_message,
      userFirstName: action.user?.first_name as string,
      dateTime: moment(action.created_at).valueOf(),
    });
  });

  configActions.forEach((action) => {
    arr.push({
      id: action.id,
      type: 'config',
      name: action.config_name,
      activityType: action.action_type,
      activityMessage: action.action_message,
      userFirstName: action.user?.first_name as string,
      dateTime: moment(action.created_at).valueOf(),
    });
  });

  arr = numericSortObjectArray(arr, 'dateTime');

  return arr;
};

const ActivityProvider = (props: IActivityProviderProps) => {
  const { children, projectId } = props;
  const { token } = useContext(AuthenticationContext);
  const { onSubscribe, onClearMessages, messages, available } = useContext(ActionCableContext);

  const [fetch, setFetch] = useState(true);
  const [activities, setActivities] = useState<ActivityData[]>([]);
  const [subscribed, setSubscribed] = useState<boolean>(false);

  useEffect(() => {
    if (fetch && token) {
      trackPromise(getResourceActions(projectId, token)).then(
        (resourceData: ResourceActionsResponse) => {
          trackPromise(getConfigActions(projectId, token))
            .then((configData: ConfigActionsResponse) => {
              setActivities(
                createActivityData(
                  resourceData.resource_actions,
                  configData.config_actions
                )
              );
              setFetch(false);
            })
            .catch((err) => {
              console.log(err);
            });
        }
      );
    }
  }, [fetch, projectId, token]);

  useEffect(() => {
    if (projectId && !subscribed && available) {
      onSubscribe(Number(projectId));
      setSubscribed(true);
    }
  }, [projectId, subscribed, onSubscribe, available]);

  useEffect(() => {
    if(messages[projectId] && messages[projectId].length > 0) {
      setFetch(true);
      onClearMessages(projectId);
    }
  }, [messages, projectId, onClearMessages]);

  return (
    <ActivityContext.Provider
      value={{
        activities,
      }}
    >
      {children}
    </ActivityContext.Provider>
  );
};

export { ActivityContext, ActivityProvider };
